import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import useLobby from "@/hooks/use-lobby";
import { LANG } from "@/types";

interface Product {
  unitName: string;
  quantity: number;
  unitPrice: number;
}

interface TenderQuotation {
  tender: { title: string; id: string; status: string };
  id: string;
  status: string;
  products: Product[];
  company: {
    CompanyNr: string;
    nameAr: string;
  };
}

interface QuatationsStatisticsProps {
  offerID: string;
}

const QuatationsStatistics: React.FC<QuatationsStatisticsProps> = ({
  offerID,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const { getListtenderQuotationsByOfferId } = useLobby();
  const [quotations, setQuotations] = useState<TenderQuotation[]>([]);
  const primaryColor = "#644980";
  const colors = ["#644980", "#815fb5", "#9678d3", "#b49ee4", "#d1bce8"];

  // Fetch quotations data
  useEffect(() => {
    const fetchQuotations = async () => {
      try {
        if (offerID) {
          const response = await getListtenderQuotationsByOfferId(offerID);
          if (response && response.data) {
            setQuotations(response.data);
          } else {
            setQuotations([]);
          }
        }
      } catch (error) {
        console.error("حدث خطأ أثناء جلب العروض:", error);
      }
    };

    fetchQuotations();
  }, [offerID]);

  // Aggregate statistics
  const totalQuotations = quotations.length;
  const completedQuotations = quotations.filter(
    (q) => q.status === "Completed"
  ).length;
  const averageValue =
    quotations.reduce(
      (acc, q) =>
        acc +
        q.products.reduce(
          (sum, product) => sum + product.unitPrice * product.quantity,
          0
        ),
      0
    ) / (totalQuotations || 1);

  // Prepare data for charts
  const statusCounts = quotations.reduce((acc: Record<string, number>, q) => {
    acc[q.status] = (acc[q.status] || 0) + 1;
    return acc;
  }, {});

  const quotationsByStatus = Object.keys(statusCounts).map((status) => ({
    name: status,
    y: statusCounts[status],
  }));

  const topQuotations = quotations
    .map((q) => ({
      title: q.tender.title,
      status: q.status,
      value: q.products.reduce(
        (sum, product) => sum + product.unitPrice * product.quantity,
        0
      ),
      company: q.company.CompanyNr,
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);

  // Highcharts Configuration
  const pieOptions = {
    chart: { type: "pie", backgroundColor: "transparent" },
    title: {
      text: t("statistics.quotationsByStatus"),
      style: { color: primaryColor },
    },
    series: [
      {
        name: t("statistics.Offers"),
        data: quotationsByStatus,
      },
    ],
    colors,
  };

  const barOptions = {
    chart: { type: "bar", backgroundColor: "transparent" },
    title: {
      text: t("statistics.topQuotationsByValue"),
      style: { color: primaryColor },
    },
    xAxis: {
      categories: topQuotations.map((q) => q.title),
      title: { text: null },
      labels: { style: { color: primaryColor } },
    },
    yAxis: {
      min: 0,
      title: {
        text: `${t("tender.fields.value")} (${t("checkout.summary.currency")})`,
        align: "high",
        style: { color: primaryColor },
      },
      labels: { style: { color: primaryColor } },
    },
    series: [
      {
        name: t("tender.fields.value"),
        data: topQuotations.map((q) => q.value),
      },
    ],
    colors: [primaryColor],
  };

  return (
    <div
      className="p-6 min-h-screen"
      style={{
        backgroundColor: "#f8f5fa",
        color: primaryColor,
        direction: locale === LANG.AR ? "rtl" : "ltr",
      }}
    >
      {/* Header */}
      <header className="mb-6">
        <h1 className="text-2xl font-bold" style={{ color: primaryColor }}>
          {t("statistics.quotationsStatistics")}
        </h1>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white  rounded-lg p-4 text-center">
          <h2 className="text-lg font-semibold">
            {t("statistics.totalQuotations")}
          </h2>
          <p className="text-2xl font-bold" style={{ color: primaryColor }}>
            {totalQuotations}
          </p>
        </div>
        <div className="bg-white  rounded-lg p-4 text-center">
          <h2 className="text-lg font-semibold">
            {t("statistics.completedQuotations")}
          </h2>
          <p className="text-2xl font-bold" style={{ color: "#815fb5" }}>
            {completedQuotations}
          </p>
        </div>
        <div className="bg-white  rounded-lg p-4 text-center">
          <h2 className="text-lg font-semibold">
            {t("statistics.averageValue")}
          </h2>
          <p className="text-2xl font-bold" style={{ color: "#9678d3" }}>
            {averageValue.toFixed(2)} {t("checkout.summary.currency")}
          </p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white  rounded-lg p-4">
          <HighchartsReact highcharts={Highcharts} options={pieOptions} />
        </div>
        <div className="bg-white  rounded-lg p-4">
          <HighchartsReact highcharts={Highcharts} options={barOptions} />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white  rounded-lg p-4 mt-6">
        <h2 className="text-lg font-semibold mb-4">
          {t("statistics.topQuotationsByValue")}
        </h2>
        <table className="w-full table-auto">
          <thead>
            <tr
              className="text-left"
              style={{
                color: primaryColor,
                borderBottom: `2px solid ${primaryColor}`,
              }}
            >
              <th className="py-2">{t("company.fields.address")}</th>
              <th className="py-2">{t("orders.fields.status")}</th>
              <th className="py-2">
                {t("tender.fields.value")} ({t("checkout.summary.currency")})
              </th>
              <th className="py-2">{t("checkout.fields.company")}</th>
            </tr>
          </thead>
          <tbody>
            {topQuotations.map((q, index) => (
              <tr key={index} style={{ borderBottom: `1px solid #e0d7ee` }}>
                <td className="py-2">{q.title}</td>
                <td className="py-2">{q.status}</td>
                <td className="py-2">{q.value.toFixed(2)}</td>
                <td className="py-2">{q.company}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QuatationsStatistics;
